import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { AngularFirestore, DocumentReference } from '@angular/fire/firestore';
import { AngularFireFunctions } from '@angular/fire/functions';
import firebase from 'firebase/app';
import { ErrorMonitoringService } from '../error-monitoring/error-monitoring.service';
import { AngularFireStorage } from '@angular/fire/storage';
import * as moment from 'moment-timezone';

@Injectable({
  providedIn: 'root'
})
export class WalletService {

  usersRef = 'userProfile';
  walletsRef = 'wallets';
  rechargesRef = 'recharges';
  walletPaymentsRef = 'payments';
  servicesRef = 'services';

  constructor(
    private angularFirestore: AngularFirestore,
    private angularFireFunctions: AngularFireFunctions,
    private storage: AngularFireStorage,
    private errorMonitoringService: ErrorMonitoringService,
  ) { }

  // crea la billetera
  createWallet(wallet: any): Promise<DocumentReference> {
    return this.angularFirestore.collection<any>(this.walletsRef).add(wallet);
  }

  // obtiene los datos de la billetera
  getWallet(walletId: string) {
    return this.angularFirestore.doc<any>(`wallets/${walletId}`).snapshotChanges().pipe(
      map(walletData => {
        const data = walletData.payload.data();
        const id = walletData.payload.id;
        if (data) {
          return { id, ...data };
        } else {
          return null;
        }
      })
    );
  }

  // obtiene las billeteras a las que pertenece el usuario
  getWallets(email: string) {
    return this.angularFirestore.collection<any>(this.walletsRef, ref => ref.where(
      'email', '==', email
    )).snapshotChanges().pipe(
      map(wallets => {
        return wallets.map(wallet => {
          const data = wallet.payload.doc.data();
          const id = wallet.payload.doc.id;
          return { id, ...data };
        });
      })
    );
  }

  // obtiene las billeteras a las que pertenece el usuario
  getWalletsWithoutObservable(userId: string) {
    return this.angularFirestore.collection<any>(this.walletsRef, ref => ref.where(
      'members', 'array-contains', userId
    )).get();
  }

  // obtiene id de wallet de un cliente
  getWalletId(userId: string) {
    return this.angularFirestore.collection<any>(this.walletsRef, ref => ref.where('admin', '==', userId)).get();
  }

  // crea una transacción en el wallet
  createWalletTransaction(transaction: any): Promise<DocumentReference> {
    return this.angularFirestore.collection<any>(this.rechargesRef).add(transaction);
  }


  // obtiene todos los pagos de servicios
  getPayments(wallet: string, limnum: number) {
    return this.angularFirestore.collection<any>(
      this.servicesRef, ref => ref.where('wallet', '==', wallet).orderBy('dates.serviceDate', 'desc').limit(limnum)
    ).snapshotChanges().pipe(
      map(payments => {
        return payments.map(payment => {
          const docRef = payment.payload.doc;
          const data = payment.payload.doc.data();
          const id = payment.payload.doc.id;
          return { id, ...data, docRef };
        });
      })
    );
  }

  // obtiene más pagos
  getMorePayments(wallet: string, limnum: number, lastDoc: firebase.firestore.DocumentSnapshot) {
    return this.angularFirestore.collection<any>(
      this.servicesRef, ref => ref
        .where('wallet', '==', wallet)
        .limit(limnum)
        .orderBy('dates.serviceDate', 'desc')
        .startAfter(lastDoc)
    ).snapshotChanges().pipe(
      map(payments => {
        return payments.map(payment => {
          const docRef = payment.payload.doc;
          const data = payment.payload.doc.data();
          const id = payment.payload.doc.id;
          return { id, ...data, docRef };
        });
      })
    );

  }

  /** obtiene todos los pagos de servicios filtrados por estado */
  getWalletPaymentsByState(wallet: string, status: string) {
    return this.angularFirestore.collection<any>(
      this.walletPaymentsRef, ref => ref.where('fromWallet', '==', wallet).where('status', '==', status)
    ).get();
  }

  /** obtiene recargas */
  getRecharges(userId: string, limnum: number) {
    return this.angularFirestore.collection<any>(
      this.rechargesRef, ref => ref.where('userId', '==', userId).orderBy('eventDate', 'desc').limit(limnum)
    ).snapshotChanges().pipe(
      map(transactions => {
        return transactions.map(transaction => {
          const docRef = transaction.payload.doc;
          const data = transaction.payload.doc.data();
          const id = transaction.payload.doc.id;
          return { id, ...data, docRef };
        });
      })
    );
  }

  // obtiene mas recargas
  getMoreRecharges(userId: string, limnum: number, lastDoc: firebase.firestore.DocumentSnapshot) {
    return this.angularFirestore.collection<any>(
      this.rechargesRef, ref => ref
        .where('userId', '==', userId)
        .limit(limnum)
        .orderBy('eventDate', 'desc')
        .startAfter(lastDoc)
    ).snapshotChanges().pipe(
      map(withdraws => {
        return withdraws.map(withdraw => {
          const docRef = withdraw.payload.doc;
          const data = withdraw.payload.doc.data();
          const id = withdraw.payload.doc.id;
          return { id, ...data, docRef };
        });
      })
    );

  }

  // obtiene el pago por ID
  getPaymentById(id: string) {
    return this.angularFirestore.collection<any>(
      this.walletPaymentsRef
    ).doc(id).valueChanges();
  }

  // obtiene la transacción por ID
  getTransactionById(id: string) {
    return this.angularFirestore.collection<any>(
      this.rechargesRef
    ).doc(id).valueChanges();
  }

  // agrega un miembro a la billetera
  addMember(wallet: string, userId: string) {
    return this.angularFirestore.collection<any>(this.walletsRef).doc(wallet).update({
      members: firebase.firestore.FieldValue.arrayUnion(userId)
    });
  }

  uploadReceipt(wallet: string, file: any) {
    return new Promise((resolve, reject) => {
      let filePath;
      // Ruta en la que se almacena
      filePath = `cash_recharges/${wallet}/${file.name}`;
      // Almacena la imagen
      this.storage.upload(filePath, file).then(snapshot => {
        // Obtiene la url de descarga
        snapshot.ref.getDownloadURL().then((downloadURL) => {
          resolve(downloadURL);
        });
      }).catch(error => {
        this.errorMonitoringService.sendError(error, 'uploadFile');
        reject();
      });
    });
  }

  updateTransaction(transactionId: string, data: any) {
    return this.angularFirestore.collection<any>(this.rechargesRef).doc(transactionId).update(data);
  }

  createWalletWhenValidate(user: any) {
    if (user) {
        const newWallet = {
            email: user.email,
            name: `${user.name} ${user.surname}`,
            admin: user.uid,
            balance: 0,
            creationDate: moment().tz('America/Bogota').format(),
            members: [user.uid]
        }
        return this.angularFirestore.collection(`wallets`).add(newWallet).catch(err => {
            this.errorMonitoringService.sendError(err, 'createWalletWhenValidate')
        });
    };
  }

  // llama a una función
  callFunction(name: string, data: any) {
    const callable = this.angularFireFunctions.httpsCallable(name);
    return callable(data);
  }

}
